import React from 'react';
import { AlertTriangle, RotateCcw, Home } from 'lucide-react';

/**
 * GIWAHS — Global Error Boundary
 *
 * Wraps the app tree beneath the Layout so any uncaught render/effect
 * error surfaces as a calm, branded recovery screen rather than a
 * blank obsidian void. Preserves D-095 identity: obsidian background,
 * Founder Gold accent, hairline gold border, platinum ivory copy.
 *
 * Behaviour:
 *   - Catches errors in the child React tree via componentDidCatch.
 *   - Logs to `console.error` for developer diagnosis (dev-only side
 *     channel — no external telemetry introduced).
 *   - User-facing UI shows only the approved production-safe status
 *     message. Raw thrown error text, stack traces, component names,
 *     file paths, and runtime exception details are NOT surfaced.
 *   - Offers two recovery paths: "Try again" (re-mount subtree by
 *     resetting the boundary's internal state) and "Return home"
 *     (hard navigation to /).
 *   - Uses window.location for Return home instead of react-router so
 *     even a broken router tree can escape.
 *
 * No business logic. No new features. Preserved routing and app
 * architecture: the boundary is a pure passthrough when no error is
 * present.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Single-site log for developer diagnosis. No external telemetry.
    // eslint-disable-next-line no-console
    console.error('[GIWAHS ErrorBoundary]', error, info?.componentStack);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  handleHome = () => {
    // Hard navigation so a broken router tree can still escape.
    window.location.assign('/');
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        data-testid="error-boundary-fallback"
        role="alert"
        aria-live="assertive"
        className="surface-obsidian"
      >
        <section className="border-b border-hairline">
          <div className="mx-auto grid max-w-[1440px] gap-12 px-5 py-20 md:grid-cols-12 md:px-8 md:py-28">
            <div className="md:col-span-8">
              <span className="label-overline text-gold inline-flex items-center gap-2">
                <AlertTriangle size={12} strokeWidth={1.5} />
                / System · Interrupted
              </span>
              <h1
                className="mt-4 font-display tracking-tighter text-platinum"
                style={{ fontWeight: 700, fontSize: 'clamp(2.5rem, 6vw, 5rem)', lineHeight: 0.95 }}
              >
                Something didn&rsquo;t load correctly.
              </h1>
              <p className="mt-6 max-w-2xl text-lg text-platinum-2">
                A component in the platform hit an unexpected error and stopped rendering. Your data, your account, and every other page are unaffected. Try the recovery paths below — or contact the desk if the issue persists.
              </p>

              <div className="mt-10 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  data-testid="error-boundary-retry-btn"
                  onClick={this.handleRetry}
                  className="btn-giwahs btn-giwahs-primary"
                >
                  <RotateCcw size={14} strokeWidth={1.5} /> Try again
                </button>
                <button
                  type="button"
                  data-testid="error-boundary-home-btn"
                  onClick={this.handleHome}
                  className="btn-giwahs btn-giwahs-secondary"
                >
                  <Home size={14} strokeWidth={1.5} /> Return home
                </button>
              </div>
            </div>

            <aside className="md:col-span-4">
              <div className="giwahs-auth-panel p-6 md:p-8">
                <span className="label-overline text-gold">Status</span>
                <p
                  data-testid="error-boundary-message"
                  className="mt-3 text-sm text-platinum-2"
                >
                  We encountered an unexpected issue while loading this page. No action has been taken on your account.
                </p>
                <p className="mt-3 text-sm text-muted-warm">
                  You can try again or return home.
                </p>
              </div>
            </aside>
          </div>
        </section>
      </div>
    );
  }
}

export default ErrorBoundary;
